import {Component, DestroyRef, EventEmitter, Input, computed, effect, inject, OnDestroy, OnInit, Output, signal, untracked} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {Subject, merge} from 'rxjs';
import {FormArray, FormGroup} from '@angular/forms';
import {Option, CustomValidators} from '@xmedical/ui';
import {
  AddressFormShape,
  BillingContactFormShape,
  ContactNumberFormShape,
  DoctorFormShape,
  EmailAddressFormShape,
  MedicalAidFormShape,
  PatientForm,
  PatientFormService,
  SecondaryContactFormShape,
  formValueToCreatePayload,
  formValueToUpdatePayload,
  makeContactNumberFormGroup,
  makeDoctorFormGroup,
  makeSecondaryContactFormGroup,
} from './patient-form.service';
import {catchError, debounceTime, distinctUntilChanged, filter, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {of} from 'rxjs';
import {CoreService} from 'src/app/core/services/core.service';
import {TabsStore} from 'src/app/core/state';
import {DatePipe} from '@angular/common';
import {Patient, PermissionProvider, Permissions, QueryPatient, PatientRepositoryService, PatientStatus} from '@xmedical/core';
import {AuthenticationService} from 'src/app/core/auth/authentication.service';
import {PatientDraft, PatientDraftPersistence} from './patient-draft-persistence.service';
import {PatientStore} from '../../../core/state';

const oldDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
const today = new Date();

// Rebuild a FormArray from a saved draft array. Empty/missing input keeps the
// form's existing default rows; otherwise the array is cleared and one fresh
// group is pushed per saved item via the supplied factory, then patched.
function rebuildArray<T extends FormGroup<any>>(
  formArray: FormArray<T>,
  savedItems: any[] | undefined,
  pushFactory: () => void,
): void {
  if (!Array.isArray(savedItems) || savedItems.length === 0) {
    return;
  }
  formArray.clear();
  savedItems.forEach((item, i) => {
    pushFactory();
    formArray.at(i)?.patchValue(item);
  });
}

@Component({
    exportAs: 'appPatientForm',
    selector: 'app-patient-form',
    templateUrl: './patient-form.component.html',
    styleUrls: ['./patient-form.component.scss'],
    standalone: false
})
export class PatientFormComponent implements OnInit, OnDestroy {

  private destroyRef = inject(DestroyRef);
  private datePipe = inject(DatePipe);
  private patientFormService = inject(PatientFormService);
  private coreService = inject(CoreService);
  private patientsRepository = inject(PatientRepositoryService);
  private tabsStore = inject(TabsStore);
  private authService = inject(AuthenticationService);
  private draftPersistence = inject(PatientDraftPersistence);
  private permissionProvider = inject(PermissionProvider);
  private patientStore = inject(PatientStore);

  // Bumps when permissions finish loading. Drives canWrite.
  private permissionsTick = toSignal(
    this.permissionProvider.permissionsLoaded$.pipe(startWith<void>(undefined)),
    { initialValue: undefined },
  );

  // True when the current user can write to this patient. Used by the form's
  // own action buttons so the existing structural `*xuiPermissionRequired`
  // gates can collapse into a single template gate, and by the read-only
  // gate effect below.
  readonly canWrite = computed(() => {
    this.permissionsTick();
    return this.permissionProvider.hasPermission(Permissions.ClinicalPatientsWrite);
  });

  // Read-only gate: toggle the entire FormGroup disabled when the user lacks
  // write permission. Emits no value/status changes so the duplicate checker
  // and draft autosave do not fire as a side effect of the toggle.
  // The guard handles the lifecycle gap before `ngOnInit` builds the form.
  private readonly canWriteGate = effect(() => {
    this.canWrite();
    this.applyCanWriteGate();
  });

  // Tracks the saveCommand counter PatientStore last advanced. The first
  // effect run captures whatever counter value was visible at mount and
  // takes no action — only subsequent bumps from `requestSave()` fire save().
  private lastSaveCommandSeen: number | null = null;
  private readonly saveCommandEffect = effect(() => {
    const tick = this.patientStore.saveCommand();
    if (this.lastSaveCommandSeen === null) {
      this.lastSaveCommandSeen = tick;
      return;
    }
    if (tick !== this.lastSaveCommandSeen) {
      this.lastSaveCommandSeen = tick;
      untracked(() => this.save());
    }
  });

  // Tracks the statusUpdateCommand serial. Only meaningful in edit mode —
  // create mode ignores incoming status pushes (the create payload has no
  // status field). Re-selecting the same status id still triggers because
  // the serial advances on every request.
  private lastStatusSerialSeen: number | null = null;
  private readonly statusUpdateCommandEffect = effect(() => {
    const cmd = this.patientStore.statusUpdateCommand();
    if (this.lastStatusSerialSeen === null) {
      this.lastStatusSerialSeen = cmd.serial;
      return;
    }
    if (cmd.serial !== this.lastStatusSerialSeen) {
      this.lastStatusSerialSeen = cmd.serial;
      if (this.isEditForm) {
        untracked(() => this.updateStatus(cmd.id));
      }
    }
  });

  private applyCanWriteGate(): void {
    if (!this.form) return;
    if (this.canWrite()) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
    }
  }

  // Emits whenever the form is replaced (clearSavedFormData rebuilds it). Form-
  // tied subscriptions pipe `takeUntil(formReset$)` so they detach when the
  // underlying form is gone, and `formEventHandler()` re-subscribes against the
  // new form. Component-lifetime streams use `takeUntilDestroyed(destroyRef)`.
  private formReset$ = new Subject<void>();

  form: PatientForm = null;

  // Local UI state — signals so child OnPush bindings see updates without
  // depending on the parent's CD cycle.
  readonly validate = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly duplicates = signal<QueryPatient[]>([]);
  readonly showFullErrorMessage = signal(false);

  // Bumped on every form.statusChanges emission. Drives any computed signals
  // that reflect form validity / per-array errors / fullFormError.
  private readonly formStatusTick = signal(0);

  readonly fullFormError = computed<any[]>(() => {
    if (!this.showFullErrorMessage()) {
      return [];
    }
    this.formStatusTick();
    return this.patientFormService.printAllFormErrors();
  });

  readonly formValid = computed(() => {
    this.formStatusTick();
    return !!this.form && this.form.valid;
  });

  readonly hasDuplicates = computed(() => this.duplicates().length > 0);

  readonly hasContactMethodError = computed(() => {
    this.formStatusTick();
    if (!this.form) return false;
    return this.hasAtLeastOneValueRequiredError(this.contactNumbers)
      || this.hasAtLeastOneValueRequiredError(this.emailAddresses)
      || this.hasAtLeastOneValueRequiredError(this.addressDetails);
  });

  readonly hasBillingContactMethodError = computed(() => {
    this.formStatusTick();
    if (!this.form) return false;
    return this.hasAtLeastOneValueRequiredError(this.billingContactContactNumbers)
      || this.hasAtLeastOneValueRequiredError(this.billingContactEmailAddresses)
      || this.hasAtLeastOneValueRequiredError(this.billingContactAddressDetails);
  });

  languages: Option[] = [];
  gender: Option[] = [];
  races: Option[] = [];
  titles: Option[] = [];
  statuses: PatientStatus[] = [];
  @Input() patient: Patient;
  @Output() patientNameChange: EventEmitter<string> = new EventEmitter();
  /**
   * Fires after a successful save. Containers (NewPatient / EditPatient) use
   * this to drive post-save navigation without going through the event bus.
   * `kind: 'created'` carries the CreatedPatient response so the container
   * can reach the new profileId; `kind: 'updated'` is a simple notification.
   */
  @Output() saved = new EventEmitter<{ kind: 'created'; created: any } | { kind: 'updated' }>();
  private debounceTimeOut = 1500;
  private debounceTimeOutShort = 100;

  // Reads the current practice settings signal directly — no snapshot field.
  get useFileNumberAsPatientProfileId(): boolean {
    return this.coreService.practiceSettings()?.useFileNumberAsPatientProfileId ?? false;
  }

  get hasTabs(): boolean {
    return this.tabsStore.hasTabs();
  }

  get isEditForm(): boolean {
    return !!this.patient;
  }

  get minDobValue(): Date {
    return oldDate;
  }

  get maxDobValue(): Date {
    return today;
  }

  get addressDetails(): FormArray<FormGroup<AddressFormShape>> {
    return this.form.controls.addressDetails;
  }

  get emailAddresses(): FormArray<FormGroup<EmailAddressFormShape>> {
    return this.form.controls.emailAddresses;
  }

  get contactNumbers(): FormArray<FormGroup<ContactNumberFormShape>> {
    return this.form.controls.contactNumbers;
  }

  get secondaryContacts(): FormArray<FormGroup<SecondaryContactFormShape>> {
    return this.form.controls.secondaryContacts;
  }

  get familyDoctors(): FormArray<FormGroup<DoctorFormShape>> {
    return this.form.controls.familyDoctors;
  }

  get referringDoctors(): FormArray<FormGroup<DoctorFormShape>> {
    return this.form.controls.referringDoctors;
  }

  get billingContact(): FormGroup<BillingContactFormShape> {
    return this.form.controls.billingContact;
  }

  get billingContactAddressDetails(): FormArray<FormGroup<AddressFormShape>> {
    return this.billingContact.controls.billingContactAddressDetails;
  }

  get billingContactContactNumbers(): FormArray<FormGroup<ContactNumberFormShape>> {
    return this.billingContact.controls.billingContactContactNumbers;
  }

  get billingContactEmailAddresses(): FormArray<FormGroup<EmailAddressFormShape>> {
    return this.billingContact.controls.billingContactEmailAddresses;
  }

  get medicalAid(): FormGroup<MedicalAidFormShape> {
    return this.form.controls.medicalAid;
  }

  get urlName(): string {
    return this.patient.name.replace(/\([^)]*\)/g, '').replace(/\s\s+/g, ' ').split(' ').join('-');
  }

  get backButtonRoute(): string {
    if (this.isEditForm) {
      return `/patients/${this.patient.profileId}/${this.urlName}`;
    } else {
      return '/dashboard';
    }
  }

  private hasAtLeastOneValueRequiredError(formArray: FormArray<FormGroup<any>>): boolean {
    return formArray.controls.some(control => control.get('number')?.hasError('atLeastOnValueRequired') ||
                                              control.get('emailAddress')?.hasError('atLeastOnValueRequired') ||
                                              control.get('address')?.hasError('atLeastOnValueRequired'));
  }

  ngOnInit(): void {
    this.languages = this.coreService.patientLanguages().map(language => ({value: language.code, text: language.name}));
    this.gender = this.coreService.patientGenders().map(gender => ({value: gender.code, text: gender.name}));
    this.races = this.coreService.patientRaces().map(race => ({value: race.code, text: race.name}));
    this.titles = this.coreService.patientTitles().map(title => ({value: title.code, text: title.name}));
    this.statuses = this.coreService.patientStatuses().sort((a, b) => a.id > b.id ? 1 : -1);
    this.form = this.patientFormService.new(this.patient);
    this.loadFormFromLocalStorage();
    this.formEventHandler();
  }

  ngOnDestroy(): void {
    this.formReset$.next();
    this.formReset$.complete();
    this.patientFormService.reset();
  }

  private genericSaveErrorMessage(): string {
    return this.isEditForm
      ? 'Failed to update patient. Please try again or contact support if the problem persists.'
      : 'Failed to create patient. Please try again or contact support if the problem persists.';
  }

  toggleFullErrorMessage() {
    this.showFullErrorMessage.update(v => !v);
  }

  save() {
    this.saveError.set(null);

    if (this.formValid()) {
      this.savePatient();
    }
    this.validate.set(true);
    this.patientFormService.validateAllFormFields();
  }

  dismissError() {
    this.saveError.set(null);
  }

  setBillingDetailsToPatientDetails() {
    this.billingContact.controls.billingContactFirstName.patchValue(this.form.controls.firstName.value);
    this.billingContact.controls.billingContactSurname.patchValue(this.form.controls.surname.value);
    this.billingContact.controls.billingContactIdentityNumber.patchValue(this.form.controls.identityNumber.value);
    this.billingContact.controls.billingContactIdentityNumberConfirmation.patchValue(this.form.controls.identityNumberConfirmation.value);

    // Clear and rebuild FormArrays - patchValue/setValue doesn't work properly on FormArrays
    this.billingContactContactNumbers.clear();
    this.contactNumbers.controls.forEach(control => {
      this.patientFormService.addBillingContactContactNumber();
      this.billingContactContactNumbers.at(this.billingContactContactNumbers.length - 1).patchValue(control.value);
    });

    this.billingContactAddressDetails.clear();
    this.addressDetails.controls.forEach(control => {
      this.patientFormService.addBillingContactAddress();
      this.billingContactAddressDetails.at(this.billingContactAddressDetails.length - 1).patchValue(control.value);
    });

    this.billingContactEmailAddresses.clear();
    this.emailAddresses.controls.forEach(control => {
      this.patientFormService.addBillingContactEmailAddress();
      this.billingContactEmailAddresses.at(this.billingContactEmailAddresses.length - 1).patchValue(control.value);
    });
  }

  updateStatus(id: number) {
    // Patch the form control only. The top-nav mutates its own
    // `patient.status` synchronously when the user picks an option, so the
    // bound display already reflects the choice — re-mutating it here would
    // cause ExpressionChangedAfterItHasBeenCheckedError on the same input.
    this.form.controls.status.setValue(id);
  }

  editPatient(patient: QueryPatient) {
    this.form.reset();
    this.tabsStore.openTabWithPatientInEditMode(patient);
  }

  private get draftContext() {
    return {
      practiceId: this.coreService.selectedPracticeId() ?? 'unknown',
      userId: this.authService.getUserId() ?? 'unknown',
    };
  }

  private saveFormToLocalStorage(): void {
    if (this.isEditForm) {
      return;
    }
    this.draftPersistence.save(this.form.getRawValue(), this.draftContext);
  }

  private loadFormFromLocalStorage(): void {
    if (this.isEditForm) {
      return;
    }
    const draft = this.draftPersistence.load(this.draftContext);
    if (!draft) {
      return;
    }
    this.applyDraft(draft);
  }

  private applyDraft(draft: PatientDraft): void {
    // Patch scalars first; FormArrays cannot be patched wholesale, so they are
    // rebuilt below. Scalar groups (medicalAid, billingContact scalars) ride
    // along with the top-level patchValue.
    const scalars: Record<string, any> = { ...draft };
    delete scalars.contactNumbers;
    delete scalars.emailAddresses;
    delete scalars.addressDetails;
    delete scalars.secondaryContacts;
    delete scalars.familyDoctors;
    delete scalars.referringDoctors;
    if (scalars.billingContact) {
      const billing = { ...scalars.billingContact };
      delete billing.billingContactContactNumbers;
      delete billing.billingContactEmailAddresses;
      delete billing.billingContactAddressDetails;
      scalars.billingContact = billing;
    }
    this.form.patchValue(scalars);

    rebuildArray(this.contactNumbers, draft.contactNumbers, () => this.patientFormService.addContactNumber());
    rebuildArray(this.emailAddresses, draft.emailAddresses, () => this.patientFormService.addEmailAddress());
    rebuildArray(this.addressDetails, draft.addressDetails, () => this.patientFormService.addAddress());

    rebuildArray(this.familyDoctors, draft.familyDoctors as any[] | undefined,
      () => this.familyDoctors.push(makeDoctorFormGroup()));
    rebuildArray(this.referringDoctors, draft.referringDoctors as any[] | undefined,
      () => this.referringDoctors.push(makeDoctorFormGroup()));

    // Secondary contacts contain a nested contactNumbers FormArray. The default
    // group has one inner row, but a saved secondary may have several. Rebuild
    // the inner array to match the saved length before patching, otherwise
    // patchValue on a FormArray silently drops entries past the existing length.
    const savedSecondaries = (draft.secondaryContacts ?? []) as any[];
    if (savedSecondaries.length > 0) {
      this.secondaryContacts.clear();
      savedSecondaries.forEach(s => {
        const group = makeSecondaryContactFormGroup();
        const savedNumbers = (s?.contactNumbers ?? []) as any[];
        if (savedNumbers.length > 0) {
          group.controls.contactNumbers.clear();
          savedNumbers.forEach(() => group.controls.contactNumbers.push(makeContactNumberFormGroup()));
        }
        this.secondaryContacts.push(group);
        this.secondaryContacts.at(this.secondaryContacts.length - 1)?.patchValue(s);
      });
    }

    const billing = draft.billingContact;
    if (billing) {
      rebuildArray(this.billingContactContactNumbers, billing.billingContactContactNumbers, () => this.patientFormService.addBillingContactContactNumber());
      rebuildArray(this.billingContactEmailAddresses, billing.billingContactEmailAddresses, () => this.patientFormService.addBillingContactEmailAddress());
      rebuildArray(this.billingContactAddressDetails, billing.billingContactAddressDetails, () => this.patientFormService.addBillingContactAddress());
    }
  }

  clearFormFromLocalStorage(): void {
    this.draftPersistence.clear(this.draftContext);
  }

  clearSavedFormData(): void {
    this.clearFormFromLocalStorage();

    // Detach all form-tied subscriptions from the old form, rebuild the form,
    // then re-subscribe against the fresh instance. The new form starts
    // enabled by default; re-apply the read-only gate so canWrite=false
    // users don't get an editable form back.
    this.formReset$.next();
    this.form = this.patientFormService.new(null);
    this.formEventHandler();
    this.applyCanWriteGate();

    this.saveError.set(null);
    this.validate.set(false);
    this.duplicates.set([]);
    this.showFullErrorMessage.set(false);
    // Bump the status-tick signal so computed signals (formValid, hasContact-
    // MethodError, hasBillingContactMethodError, fullFormError) recompute
    // against the new form immediately. Without this, they keep returning
    // their last cached value until the new form emits its first
    // statusChanges event.
    this.formStatusTick.update(v => v + 1);
  }

  private savePatient() {
    const value = this.form.getRawValue();
    if (this.isEditForm) {
      const payload = formValueToUpdatePayload(value, this.patient);
      this.patientStore.update(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => this.saved.emit({ kind: 'updated' }),
        error: err => this.handleSaveError(err)
      });
    } else {
      const payload = formValueToCreatePayload(value);
      this.patientStore.create(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: created => {
          // Mirror the legacy PatientService.createPatient side-effects: open
          // the new patient in a tab, and clear the localStorage draft.
          this.tabsStore.openTabWithNewPatient(created);
          this.clearFormFromLocalStorage();
          this.saved.emit({ kind: 'created', created });
        },
        error: err => this.handleSaveError(err)
      });
    }
  }

  /**
   * Map API errors from a failed PatientStore.create() / .update() onto the
   * form. Replaces the legacy `patientEventService.error$` subscription that
   * read from a fan-out event bus. The argument is whatever the observable
   * errored with — typically an HttpErrorResponse where `.error` is the
   * structured API payload.
   */
  private handleSaveError(err: any): void {
    const apiErrorBody = err?.error;
    if (!apiErrorBody) {
      this.saveError.set(this.genericSaveErrorMessage());
      return;
    }
    const result = CustomValidators.applyApiErrorsToForm(this.form, apiErrorBody);
    if (result.applied.length > 0) {
      this.saveError.set('Please correct the highlighted fields and try again.');
      this.validate.set(true);
    } else if (result.unmappedErrors.length > 0) {
      const errorMessages = result.unmappedErrors.flatMap(e => e.messages);
      this.saveError.set(errorMessages.join('. '));
    } else {
      this.saveError.set(this.genericSaveErrorMessage());
    }
  }

  private formEventHandler() {
    // Form-tied streams complete on `formReset$` (when clearSavedFormData
    // rebuilds the form) and on component destroy.
    const untilFormReset = <T>() => takeUntil<T>(merge(this.formReset$));

    this.form.controls.firstName.valueChanges.pipe(
      debounceTime(this.debounceTimeOut),
      distinctUntilChanged(),
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.formValueChangeHandler());

    this.form.controls.surname.valueChanges.pipe(
      debounceTime(this.debounceTimeOut),
      distinctUntilChanged(),
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.formValueChangeHandler());

    this.form.controls.identityNumber.valueChanges.pipe(
      debounceTime(this.debounceTimeOutShort),
      distinctUntilChanged(),
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.idControlStatusChangedHandler());

    // Duplicate-patient check: switchMap cancels any in-flight request when a
    // newer query supersedes it. No more bespoke `searching` flag.
    const duplicateTrackedFields: Array<keyof PatientForm['controls']> =
      ['firstName', 'surname', 'identityNumber', 'passportNumber', 'medicalAid', 'contactNumbers', 'emailAddresses', 'dateOfBirth'];
    this.form.valueChanges.pipe(
      filter(() => !this.isEditForm && duplicateTrackedFields.some(f => this.form.controls[f].dirty)),
      debounceTime(this.debounceTimeOut),
      distinctUntilChanged(),
      switchMap(() => {
        const query = this.buildDuplicateQuery();
        if (!query) {
          return of([] as QueryPatient[]);
        }
        return this.patientsRepository.findSimilar(query).pipe(catchError(error => {
          console.error(error);
          return of([] as QueryPatient[]);
        }));
      }),
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(results => this.duplicates.set(results));

    if (!this.isEditForm) {
      this.form.valueChanges.pipe(
        debounceTime(this.debounceTimeOut),
        distinctUntilChanged(),
        untilFormReset(),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => this.saveFormToLocalStorage());
    }

    this.form.statusChanges.pipe(
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.formStatusTick.update(v => v + 1));
  }

  private idControlStatusChangedHandler() {
    const idControl = this.form.controls.identityNumber;
    if (idControl.valid && idControl.value && idControl.value.length > 5) {
      const identityValueString = idControl.value;
      let dobString = identityValueString.substr(0, 2) + '-' + identityValueString.substr(2, 2) + '-' + identityValueString.substr(4, 2);
      const year = dobString.split('-')[0];
      const currentYear = new Date().getFullYear().toString().slice(-2);
      const genderCode = identityValueString.substr(6, 1);
      const gender = parseInt(genderCode, 10) < 5 ? 'F' : 'M';
      const dobControl = this.form.controls.dateOfBirth;
      const genderControl = this.form.controls.gender;
      dobString = year < currentYear ? '20' + dobString : 19 + dobString;
      dobControl.setValue(dobString);
      dobControl.updateValueAndValidity();
      genderControl.setValue(gender);
      genderControl.updateValueAndValidity();
    }
  }

  private formValueChangeHandler() {
    const firstName = this.form.controls.firstName.value || '';
    const surname = this.form.controls.surname.value || '';
    const name = firstName + ' ' + surname;
    if (name === ' ') {
      return;
    }
    this.patientNameChange.emit(name);
  }

  private buildDuplicateQuery(): string {
    const c = this.form.controls;
    const queryParams: string[] = [];
    if (c.firstName.value) { queryParams.push('firstName=' + c.firstName.value); }
    if (c.surname.value) { queryParams.push('surname=' + c.surname.value); }
    if (c.dateOfBirth.value) {
      const dobString = this.datePipe.transform(c.dateOfBirth.value, 'yyyy-MM-dd 00:00:00');
      queryParams.push('dateOfBirth=' + dobString);
    }
    if (c.identityNumber.value) { queryParams.push('identityNumber=' + c.identityNumber.value); }
    if (c.passportNumber.value) { queryParams.push('passportNumber=' + c.passportNumber.value); }
    if (this.medicalAid.controls.medicalAidNumber.value) {
      queryParams.push('medicalAid.medicalAidNumber=' + this.medicalAid.controls.medicalAidNumber.value || '');
    }
    if (this.medicalAid.controls.dependantCode.value) {
      queryParams.push('medicalAid.dependantCode=' + this.medicalAid.controls.dependantCode.value || '');
    }
    this.contactNumbers.controls.forEach(group => {
      const v = group.controls.number.value;
      if (v) {
        queryParams.push('contactNumbers.' + group.controls.label.value + '=' + v.replace(/\s/g, ''));
      }
    });
    this.emailAddresses.controls.forEach(group => {
      const v = group.controls.emailAddress.value;
      if (v) {
        queryParams.push('emailAddresses.' + group.controls.label.value + '=' + v);
      }
    });
    return queryParams.filter(p => p.length > 0).join(',');
  }
}
